import { useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'

import AvatarCircle from './AvatarCircle.jsx'
import { MemberLink, People } from './Person.jsx'
import WatchScreenTabs from './WatchScreenTabs.jsx'
import {
  MISCONFIGURED as ARCHIVE_MISCONFIGURED,
  READY as ARCHIVE_READY,
  UNAVAILABLE as ARCHIVE_UNAVAILABLE,
  useWatchArchive,
} from './watchArchive.js'
import {
  DEFAULT_CONTENT_BOARD,
  DEFAULT_METRIC,
  DEFAULT_PERIOD,
  badgeCards,
  boardMemberRows,
  boardMetricTabs,
  boardPeriodTabs,
  contentBoardEmptyText,
  contentBoardNote,
  contentBoardRows,
  contentBoardTabs,
  contentBoardsPublished,
  formatBoardValue,
  gameOf,
  hallOfFameCards,
  metricNote,
} from './watchGame.js'
import { memberProfilePath } from './watch.js'
import { LOADING as SESSION_LOADING, SIGNED_IN, signInHref, useSession } from '../session.js'
import { UNKNOWN_NAME, nameFor, useDirectory } from '../directory.js'
import { useDocumentTitle } from '../useDocumentTitle.js'

// The Leaderboards view — screen 3 of docs/design-briefs/watch/v2/
// HANDOFF.md, "boards" in the prototype (`D2D Watch v2.dc.html`'s
// `isBoards` block, and its `boards()`/`memberVals()`). V2-5c.
//
// ═══════════════════════════════════════════════════════════════════════════
// V2-5c BUILT FOUR THINGS (brief §0) — THE MEMBER BOARD, THE SEASON HALL OF
// FAME, THE SCREEN TAB, AND THE PROFILE'S STAT TILES (WatchMemberProfile.
// jsx). V2-5f ADDED A FIFTH, THE BADGES SECTION. V2-5g (below) ADDS A
// SIXTH, THE CONTENT BOARDS ("WHAT THE GROUP RATES").
// ═══════════════════════════════════════════════════════════════════════════
//
// This comment used to say badges drew no section here at all, alongside
// the design's own "What the group rates" content boards (top rated,
// hidden gems, crew classics, most contrarian, best episodes) — true when
// V2-5c wrote it (`game` carried neither), stale the moment V2-5d
// published `game["badges"]`. The content boards were a real, distinct gap
// for two more slices — `game` carried no per-title content-board key at
// all until V2-5e published `game["content_boards"]` — and `ContentBoards`,
// below, is what now draws it, over `watchGame.js`'s own `contentBoardRows`
// selector rather than the design prototype's invented sample rows.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 SAME GATING SHAPE AS WatchArchive.jsx/WatchMemberProfile.jsx, ON
//    PURPOSE — ONE `game` KEY ON THE SAME BLOB, NOT A THIRD FETCH
// ═══════════════════════════════════════════════════════════════════════════
//
// Boards and hall of fame are both derived from `3a/watch-archive.json`'s
// own `game` key (`utils/watch_projection.py::KEY_GAME`) — the SAME archive
// `WatchArchive.jsx` and the member profile already fetch through
// `watchArchive.js::useWatchArchive`. This page reuses that hook rather
// than inventing a third endpoint: signed-out gets the same door, a
// misconfigured deployment gets the same sentence, and a genuinely
// unavailable archive gets the same retry copy — RECONCILIATION.md's own
// "rebuild against the design AND against what you replace" applied to a
// page that has no prior version to replace, only siblings to match.
//
// `gameOf(archive.shared)` is the one thing specific to this page: a
// `READY` archive can still carry no `game` at all (an archive published
// before V2-5a merged — `watchGame.js`'s own header). That is its own,
// fourth branch, distinct from loading/unavailable/misconfigured, because
// none of those describe it honestly.

export default function WatchLeaderboards() {
  useDocumentTitle('Leaderboards')
  const location = useLocation()
  const session = useSession()
  const signedIn = session.status === SIGNED_IN
  const archive = useWatchArchive(signedIn)
  const directory = useDirectory()

  if (session.status === SESSION_LOADING) {
    return (
      <Frame>
        <p className="d2d-notice" data-testid="boards-loading-session">
          Checking whether you are signed in.
        </p>
      </Frame>
    )
  }

  if (!signedIn) {
    return (
      <Frame>
        <p className="d2d-notice" data-testid="boards-signed-out">
          You need to be signed in to see this. <a href={signInHref(location)}>Sign in with Discord</a>.
        </p>
      </Frame>
    )
  }

  if (archive.state === ARCHIVE_MISCONFIGURED) {
    return (
      <Frame>
        <p className="d2d-notice" data-testid="boards-misconfigured">
          The archive is set up wrong on this deployment, so the leaderboards cannot be shown
          until that is fixed — this is not about your account.
        </p>
      </Frame>
    )
  }

  if (archive.state !== ARCHIVE_READY) {
    return (
      <Frame>
        <p
          className="d2d-notice"
          data-testid={archive.state === ARCHIVE_UNAVAILABLE ? 'boards-unavailable' : 'boards-loading'}
        >
          {archive.state === ARCHIVE_UNAVAILABLE
            ? 'The leaderboards are unavailable right now. Try again in a minute.'
            : 'Loading the leaderboards…'}
        </p>
      </Frame>
    )
  }

  const game = gameOf(archive.shared)
  if (!game) {
    return (
      <Frame>
        <p className="d2d-notice" data-testid="boards-not-published">
          Leaderboards have not been published yet.
        </p>
      </Frame>
    )
  }

  return (
    <Frame>
      <LeaderboardsBody game={game} directory={directory} viewerUserId={session.userId} />
    </Frame>
  )
}

function Frame({ children }) {
  return (
    <article className="d2d-page d2d-watch d2d-boards" data-testid="watch-leaderboards-page">
      <WatchScreenTabs active="leaderboards" />
      <div className="d2d-boards__head">
        <div className="d2d-micro d2d-boards__kicker">Watch</div>
        <h1 className="d2d-boards__h1">Leaderboards</h1>
      </div>
      {children}
    </article>
  )
}

function LeaderboardsBody({ game, directory, viewerUserId }) {
  const [metric, setMetric] = useState(DEFAULT_METRIC)
  const [period, setPeriod] = useState(DEFAULT_PERIOD)

  // Brief §2.2: "Toggling board or period changes the ranking without a
  // refetch." Both are plain client-side state over the ONE already-loaded
  // `game` — no request goes out when either tab changes.
  const rows = useMemo(() => boardMemberRows(game, metric, period), [game, metric, period])
  const cards = useMemo(() => hallOfFameCards(game), [game])
  const note = metricNote(metric)
  // Brief §0: "the header's meta line counts the badges, and yours when
  // signed in." `LeaderboardsBody` only ever renders once the page has
  // already gated on being signed in (`WatchLeaderboards`, above), so
  // `viewerUserId` is always a real id here — the "yours" clause is never
  // omitted from THIS render, only from the sign-in door that stands in
  // front of it (see `watch-leaderboards.test.jsx`'s own gating tests).
  const badges = useMemo(() => badgeCards(game, viewerUserId), [game, viewerUserId])
  const yoursCount = badges.filter((badge) => badge.mine).length
  const badgeMeta = `${badges.length} badges · ${yoursCount} yours`

  // V2-5g: the content boards ("What the group rates") — plain client-side
  // state over the SAME already-loaded `game`, the identical no-refetch
  // shape the member board's own metric/period tabs already use above.
  const [board, setBoard] = useState(DEFAULT_CONTENT_BOARD)
  const contentRows = useMemo(() => contentBoardRows(game, board), [game, board])

  return (
    <div className="d2d-boards__body">
      <section className="d2d-watch__section" data-testid="boards-board-section">
        <div role="tablist" aria-label="Board" className="d2d-boards__tablist">
          {boardMetricTabs(metric).map((tab) => (
            <button
              key={tab.metric}
              type="button"
              role="tab"
              aria-selected={tab.active}
              className={`d2d-boards__tab${tab.active ? ' d2d-boards__tab--active' : ''}`}
              onClick={() => setMetric(tab.metric)}
              data-testid={`boards-metric-${tab.metric}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div role="group" aria-label="Period" className="d2d-boards__periodgroup">
          {boardPeriodTabs(period).map((tab) => (
            <button
              key={tab.period}
              type="button"
              aria-pressed={tab.active}
              className={`d2d-boards__period${tab.active ? ' d2d-boards__period--active' : ''}`}
              onClick={() => setPeriod(tab.period)}
              data-testid={`boards-period-${tab.period}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <p className="d2d-micro d2d-watch__note" data-testid="boards-note">
          {note}
        </p>

        {rows.length === 0 ? (
          <p className="d2d-watch__flatline" data-testid="boards-empty">
            Nobody has a record on this board yet.
          </p>
        ) : (
          <ol className="d2d-boards__list" data-testid="boards-rows">
            {rows.map((row) => (
              <BoardRow
                key={`${period}-${metric}-${row.memberId}`}
                row={row}
                metric={metric}
                directory={directory}
                viewerUserId={viewerUserId}
              />
            ))}
          </ol>
        )}
      </section>

      <section className="d2d-watch__section" aria-label="Season hall of fame" data-testid="boards-hof-section">
        <span className="d2d-micro d2d-watchtitle__sectiontitle">Season hall of fame</span>
        {cards.length === 0 ? (
          <p className="d2d-watch__flatline" data-testid="boards-hof-empty">
            No season has closed yet.
          </p>
        ) : (
          <div className="d2d-boards__hofgrid" data-testid="boards-hof-cards">
            {cards.map((card) => (
              <HallOfFameCard key={card.key} card={card} directory={directory} />
            ))}
          </div>
        )}
      </section>

      {badges.length > 0 ? (
        <section className="d2d-watch__section" aria-label="Badges" data-testid="boards-badges-section">
          <div className="d2d-watchtitle__sectionhead">
            <span className="d2d-micro d2d-watchtitle__sectiontitle">Badges</span>
            <span className="d2d-micro d2d-watch__note" data-testid="boards-badges-meta">
              {badgeMeta}
            </span>
          </div>
          <div className="d2d-boards__badgegrid" data-testid="boards-badge-cards">
            {badges.map((badge) => (
              <BadgeCard key={badge.badgeId} badge={badge} />
            ))}
          </div>
        </section>
      ) : null}

      {contentBoardsPublished(game) ? (
        <ContentBoards
          board={board}
          rows={contentRows}
          directory={directory}
          onSelectBoard={setBoard}
        />
      ) : null}
    </div>
  )
}

/**
 * "What the group rates" — the design's own H2 above a boxed section
 * (`D2D Watch v2.dc.html`'s `boards()`, the markup around `cbTabs`/`cb`),
 * placed after Badges, matching where the design places it relative to the
 * member board, hall of fame and badges above. `contentBoardsPublished`
 * gates the WHOLE thing (never rendered per-board): an archive from before
 * V2-5e has no `content_boards` key at all, a genuinely different state
 * from "published, and this board has no qualifying row yet" — see
 * `watchGame.js::contentBoardsPublished`'s own header.
 */
function ContentBoards({ board, rows, directory, onSelectBoard }) {
  return (
    <>
      <h2 className="d2d-boards__cbheading">What the group rates</h2>
      <section className="d2d-watch__section" data-testid="boards-cb-section">
        <div role="tablist" aria-label="Content board" className="d2d-boards__tablist">
          {contentBoardTabs(board).map((tab) => (
            <button
              key={tab.boardId}
              type="button"
              role="tab"
              aria-selected={tab.active}
              className={`d2d-boards__tab${tab.active ? ' d2d-boards__tab--active' : ''}`}
              onClick={() => onSelectBoard(tab.boardId)}
              data-testid={`boards-cb-tab-${tab.boardId}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <p className="d2d-micro d2d-watch__note" data-testid="boards-cb-note">
          {contentBoardNote(board)}
        </p>

        {rows.length === 0 ? (
          <p className="d2d-watch__flatline" data-testid="boards-cb-empty">
            {contentBoardEmptyText(board)}
          </p>
        ) : (
          <ol className="d2d-boards__list" data-testid="boards-cb-rows">
            {rows.map((row) => (
              <ContentBoardRow key={`${board}-${row.rank}`} row={row} directory={directory} />
            ))}
          </ol>
        )}
      </section>
    </>
  )
}

/** One content-board row — rank, then a single link to the title's own
 * page carrying both the title and this board's stat text (brief §2: every
 * row links to `/watch/<subscription_id>`, the same literal every other
 * watch link in this codebase uses — `WatchArchive.jsx`/`WatchPanel.jsx`/
 * `WatchMemberProfile.jsx` all inline it the identical way, never through a
 * shared path helper). `row.memberIds` is non-empty only for hidden gems
 * (`watchGame.js::contentBoardRows`'s own contract) — `<People>` is what
 * resolves those to names, the same component `HallOfFameRow` already uses
 * one section up, so a departed member renders exactly as it already does
 * there. */
function ContentBoardRow({ row, directory }) {
  return (
    <li className="d2d-boards__row" data-testid="boards-cb-row">
      <span className="d2d-boards__rank" data-testid="boards-cb-row-rank">
        {row.rank}
      </span>
      <Link className="d2d-boards__cblink" to={`/watch/${row.subscriptionId}`} data-testid="boards-cb-row-link">
        <span className="d2d-boards__cbtitle" data-testid="boards-cb-row-title">
          {row.title}
        </span>
        <span className="d2d-micro d2d-boards__cbstat" data-testid="boards-cb-row-stat">
          {row.stat}
          {row.memberIds.length > 0 ? <People directory={directory} ids={row.memberIds} /> : null}
        </span>
      </Link>
    </li>
  )
}

/** One badge card — name, description, and a footer reading the member
 * count (brief §0). Ember film when it is yours (HANDOFF §3: "badges
 * ('You have it · N members' in ember film)"), a plain bordered box
 * otherwise — the same mine/not-mine split the prototype's own `b.mine`/
 * `b.notMine` draws (`D2D Watch v2.dc.html`), over the real badge instead
 * of its invented sample data. */
function BadgeCard({ badge }) {
  return (
    <div
      className={`d2d-boards__badgecard${badge.mine ? ' d2d-boards__badgecard--mine' : ''}`}
      data-testid="boards-badge-card"
    >
      <div className="d2d-boards__badgename">{badge.name}</div>
      <div className="d2d-boards__badgedesc">{badge.description}</div>
      <div
        className={`d2d-micro d2d-boards__badgefooter${badge.mine ? ' d2d-boards__badgefooter--mine' : ''}`}
        data-testid="boards-badge-footer"
      >
        {badgeFooterText(badge)}
      </div>
    </div>
  )
}

/** Brief §0, verbatim: "'You have it · N members' in the ember film when
 * it's yours, else 'N members'... with nobody holding it, 'Nobody yet'."
 * `mine` implies `count >= 1` (you are a member of your own count), so
 * "Nobody yet" is reachable only on the `notMine` side — never alongside
 * "You have it." */
function badgeFooterText(badge) {
  // Manager review: the design's "N members" was drawn with N of 2 or more;
  // with one holder it read "1 members", which is what the live store shows
  // today (one member holds First finish).
  const members = `${badge.count} ${badge.count === 1 ? 'member' : 'members'}`
  if (badge.mine) return `You have it · ${members}`
  if (badge.count === 0) return 'Nobody yet'
  return members
}

/** One ranked row — rank, Discord avatar, the name (linking to the member
 * profile), and the value with its unit (brief §0.2). `isYou` gets the
 * ember ring and ember name RECONCILIATION.md item 3 moved to ember for
 * v2 ("leaderboard ... highlights" is explicitly named under the design's
 * orange role) — never dusk, which this feature draws nowhere at all
 * (there is no progress here, only totals). */
function BoardRow({ row, metric, directory, viewerUserId }) {
  const isYou = row.memberId === viewerUserId
  const person = nameFor(directory, row.memberId)
  const initial = person.known ? person.label.trim().charAt(0).toUpperCase() : '·'
  const to = memberProfilePath(row.memberId)

  return (
    <li className={`d2d-boards__row${isYou ? ' d2d-boards__row--you' : ''}`} data-testid="boards-row">
      <span className="d2d-boards__rank" data-testid="boards-row-rank">
        {row.rank}
      </span>
      <AvatarCircle
        person={person}
        initial={initial}
        className={`d2d-avatar d2d-avatar--36 d2d-boards__avatar${isYou ? ' d2d-boards__avatar--you' : ''}`}
        testId="boards-row-avatar"
      />
      <MemberLink
        to={to}
        className={`d2d-boards__name${isYou ? ' d2d-boards__name--you' : ''}`}
        testId="boards-row-name"
      >
        {person.known ? person.label : UNKNOWN_NAME}
      </MemberLink>
      <span className="d2d-boards__value" data-testid="boards-row-value">
        {formatBoardValue(metric, row.value)}
      </span>
    </li>
  )
}

function HallOfFameCard({ card, directory }) {
  return (
    <div className="d2d-boards__hofcard" data-testid="boards-hof-card">
      <div className="d2d-boards__hofseason">{card.label}</div>
      {card.rows.map((row) => (
        <HallOfFameRow key={row.metric} row={row} directory={directory} />
      ))}
    </div>
  )
}

/** One board's leader for one closed season — brief §0.3: "the champion
 * is the hours leader ... mark it on its row, and say how." The mark is
 * the visible "Champion" pill; "how" is its `title` attribute, spelling
 * out the tiebreak (`watchGame.js::hallOfFameRow`'s own `championNote`) —
 * text an ordinary reader would otherwise have to take on faith. */
function HallOfFameRow({ row, directory }) {
  return (
    <div
      className={`d2d-boards__hofrow${row.isChampion ? ' d2d-boards__hofrow--champion' : ''}`}
      data-testid="boards-hof-row"
    >
      <span className="d2d-micro d2d-boards__hofcat">{row.label}</span>
      {row.hasLeader ? (
        <span className="d2d-boards__hofwho" data-testid="boards-hof-who">
          <People directory={directory} ids={row.memberIds} />
          {' · '}
          {row.value}
          {row.isChampion ? (
            <span
              className="d2d-micro d2d-boards__champbadge"
              data-testid="boards-champion-badge"
              title={row.championNote}
            >
              Champion
            </span>
          ) : null}
        </span>
      ) : (
        <span className="d2d-micro d2d-watch__note" data-testid="boards-hof-noleader">
          No leader yet
        </span>
      )}
    </div>
  )
}
